import React, {
    useCallback,
    useEffect,
    useReducer,
    useState,
    Fragment
} from 'react';
import { useTranslator } from 'tradera-lang/translate';
import { LocalLoader } from 'tradera-components/loader/loader';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import leaveFeedbackService from 'tradera-apps/my-tradera/app/data/leave-feedback-service';
import { LeaveFeedbackItem } from './leave-feedback-item';
import { ThinDivider, ThickDivider } from './divider';
import {
    AlreadySubmittedErrorDialog,
    SubmittedDialog,
    ConfirmSubmitDialog,
    SubmitErrorDialog
} from './dialog';
import { keepUnique } from 'tradera-utils/array';
import {
    createFeedbackPayloadsMap,
    getRatingSettings
} from './leave-feedback-helper';
import { feedbacksReducer, initialState } from './feedbacks-reducer';
import { setFeedbacks } from './feedbacks-actions';
import { ROLES } from './constants';
import {
    Breakpoint,
    BREAKPOINTS
} from 'tradera-components/breakpoint/breakpoint';
import { PageHeader } from './page-header';
import { SubmitButton } from './submit-button';
import { StickyContainer } from './sticky-container';
import styles from './leave-feedback.module.scss';

const PAGE_SIZE = 10;

const Layout = ({ renderHeader, children, className = '' }) => (
    <div className={styles.leaveFeedbackLayout}>
        <div className={className}>
            {renderHeader && renderHeader()}
            {children}
        </div>
    </div>
);

export const LeaveFeedback = ({
    isInMultipleFeedbacksContext,
    onDismiss = () => {},
    onDone,
    role,
    transactionIds: givenTransactionIds
}) => {
    const [transactionIds] = useState(givenTransactionIds);
    const [loadedCount, setLoadedCount] = useState(0);
    const [itemsLeft, setItemsLeft] = useState(0);
    const [groups, setGroups] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [negativeCount, setNegativeCount] = useState(0);
    const [showSubmitNegativeWarning, setShowSubmitNegativeWarning] = useState(
        false
    );
    const [showSubmittedDialog, setShowSubmittedDialog] = useState(false);
    const [showSubmitErrorDialog, setShowSubmitErrorDialog] = useState(false);
    const [
        showAlreadySubmittedErrorDialog,
        setShowAlreadySubmittedErrorDialog
    ] = useState(false);
    const [{ feedbacks, submittables }, dispatch] = useReducer(
        feedbacksReducer,
        initialState
    );
    const [ratingSettings] = useState(getRatingSettings(role === ROLES.BUYER));
    const nextCount = itemsLeft > PAGE_SIZE ? PAGE_SIZE : itemsLeft;
    const { t } = useTranslator();

    useEffect(() => {
        let loadedCount = groups.reduce((acc, group) => {
            return acc + group.items.length;
        }, 0);
        setLoadedCount(loadedCount);
        const feedbacks = createFeedbackPayloadsMap(
            groups,
            role === ROLES.BUYER
        );
        dispatch(setFeedbacks(feedbacks));
    }, [groups, role]);

    useEffect(() => {
        const negatives = Object.values(submittables).filter(
            submittable => submittable.isPositive === false
        );
        setNegativeCount(negatives.length);
    }, [submittables]);

    const submitOrConfirm = useCallback(() => {
        if (negativeCount > 0) {
            setShowSubmitNegativeWarning(true);
        } else {
            submit(submittables);
        }
    }, [negativeCount, submit, submittables]);

    const submit = useCallback(submittables => {
        setIsSubmitting(true);
        const onSuccess = () => {
            setIsSubmitting(false);
            setShowSubmittedDialog(true);
        };
        const onError = () => {
            setIsSubmitting(false);
            setShowSubmitErrorDialog(true);
        };
        leaveFeedbackService.submitData(submittables, onSuccess, onError);
    }, []);

    const onFetchAllSuccess = useCallback(data => {
        setGroups(oldGroups => {
            const newGroups = [...oldGroups];
            data.groups.forEach(incomingGroup => {
                const existingGroup = newGroups.find(
                    g => g.label === incomingGroup.label
                );
                if (existingGroup) {
                    const mayHaveDuplicates = existingGroup.items.concat(
                        incomingGroup.items
                    );
                    // eslint-disable-next-line better-mutation/no-mutation
                    existingGroup.items = keepUnique(
                        mayHaveDuplicates,
                        item => `${item.itemId}${item.transactionId}`,
                        false
                    );
                } else {
                    newGroups.push(incomingGroup);
                }
            });
            return newGroups;
        });
        setItemsLeft(() => data.itemsLeft);
        setIsFetching(false);
    }, []);

    const onFetchError = useCallback(data => {
        setIsFetching(false);
        if (
            data.responseJSON.errorCode === 'BuyerFeedbackAlreadyLeft' ||
            data.responseJSON.errorCode === 'SellerFeedbackAlreadyLeft'
        ) {
            setShowAlreadySubmittedErrorDialog(true);
        }
    }, []);

    const fetchNext = useCallback(() => {
        leaveFeedbackService.getAllFeedbackItems(
            loadedCount,
            PAGE_SIZE,
            onFetchAllSuccess,
            onFetchError
        );
    }, [loadedCount, onFetchError, onFetchAllSuccess]);

    const fetchAll = useCallback(() => {
        setIsFetching(true);
        leaveFeedbackService.getAllFeedbackItems(
            0,
            PAGE_SIZE,
            onFetchAllSuccess,
            onFetchError
        );
    }, [onFetchAllSuccess, onFetchError]);

    const fetchByTransactionIds = useCallback(
        transactionIds => {
            setIsFetching(true);
            leaveFeedbackService.getFeedbackItems(
                transactionIds,
                data => {
                    const group = {
                        items: data.items
                    };
                    setGroups(() => {
                        return [group];
                    });
                    setIsFetching(false);
                },
                onFetchError
            );
        },
        [onFetchError]
    );

    useEffect(() => {
        setIsFetching(true);
        leaveFeedbackService.setRootUrl(`/my/leave-feedback/${role}/`);
        if (transactionIds.length > 0) {
            fetchByTransactionIds(transactionIds);
        } else {
            fetchAll();
        }
    }, [fetchAll, fetchByTransactionIds, role, transactionIds]);

    if (isFetching) {
        return (
            <Layout className={styles.loaderContainer}>
                <LocalLoader />
            </Layout>
        );
    }

    const isInDialog = !isInMultipleFeedbacksContext;
    const isSubDialogOpen =
        showAlreadySubmittedErrorDialog ||
        showSubmitNegativeWarning ||
        showSubmittedDialog ||
        showSubmitErrorDialog;
    const showItems = !(isInDialog && isSubDialogOpen);
    const itemsExist = loadedCount > 0;
    return (
        <>
            {showSubmitNegativeWarning && (
                <ConfirmSubmitDialog
                    negativeCount={negativeCount}
                    isInMultipleFeedbacksContext={isInMultipleFeedbacksContext}
                    onConfirm={() => {
                        setShowSubmitNegativeWarning(false);
                        submit(submittables);
                    }}
                    onDismiss={() => setShowSubmitNegativeWarning(false)}
                />
            )}
            {showSubmittedDialog && (
                <SubmittedDialog
                    submittedFeedbackCount={submittables.length}
                    onDismiss={() => onDone(submittables)}
                />
            )}
            {showSubmitErrorDialog && (
                <SubmitErrorDialog
                    onDismiss={() => {
                        setShowSubmitErrorDialog(false);
                        onDismiss();
                    }}
                />
            )}
            {showAlreadySubmittedErrorDialog && (
                <AlreadySubmittedErrorDialog
                    onDismiss={() => {
                        setShowAlreadySubmittedErrorDialog(false);
                        onDismiss();
                    }}
                />
            )}
            {showItems && (
                <Layout
                    className={isInMultipleFeedbacksContext ? '' : 'bg-white'}
                    renderHeader={() =>
                        isInMultipleFeedbacksContext && (
                            <Breakpoint downTo={BREAKPOINTS.MD}>
                                <PageHeader
                                    title={t(
                                        role === ROLES.BUYER
                                            ? 'leaveFeedback_rateSeller_title'
                                            : 'leaveFeedback_rateBuyer_title'
                                    )}
                                    actionComponent={
                                        <SubmitButton
                                            isInMultipleFeedbacksContext={
                                                isInMultipleFeedbacksContext
                                            }
                                            isSubmitting={isSubmitting}
                                            onClick={submitOrConfirm}
                                            submittableCount={
                                                submittables.length
                                            }
                                        />
                                    }
                                    dataTestPageId={
                                        role === ROLES.BUYER
                                            ? 'leaveFeedback_buyer'
                                            : 'leaveFeedback_seller'
                                    }
                                />
                            </Breakpoint>
                        )
                    }>
                    {isInMultipleFeedbacksContext && (
                        <Breakpoint upTo={BREAKPOINTS.SM}>
                            <StickyContainer
                                position={'top'}
                                zIndex="19"
                                className="d-flex justify-content-center my-2">
                                <SubmitButton
                                    isInMultipleFeedbacksContext={
                                        isInMultipleFeedbacksContext
                                    }
                                    isSubmitting={isSubmitting}
                                    onClick={submitOrConfirm}
                                    submittableCount={submittables.length}
                                />
                            </StickyContainer>
                        </Breakpoint>
                    )}
                    {!itemsExist && <div>{t('leaveFeedback_no_items')}</div>}
                    {itemsExist && (
                        <div
                            className={`bg-white ${
                                isInDialog ? 'pb-3 pb-md-0' : 'py-4'
                            }`}>
                            {groups.map((group, index) => (
                                <div
                                    key={
                                        group.label ||
                                        `group-without-label-${index}`
                                    }
                                    className="mb-4">
                                    {index > 0 && (
                                        <ThickDivider className="mb-4" />
                                    )}
                                    {group.label && (
                                        <h3 className="px-2 px-md-3 text-center text-styled">
                                            {group.label}
                                        </h3>
                                    )}
                                    {group.items.map((item, index) => {
                                        const payload =
                                            feedbacks[item.transactionId];
                                        if (!payload) {
                                            return null;
                                        }
                                        return (
                                            <Fragment
                                                key={`${item.transactionId}-${item.itemId}`}>
                                                {index > 0 && (
                                                    <ThinDivider className="mb-4" />
                                                )}
                                                <LeaveFeedbackItem
                                                    comment={payload.comment}
                                                    dispatch={dispatch}
                                                    errorMessageTranslationKeys={
                                                        payload.errorMessageTranslationKeys
                                                    }
                                                    transactionId={
                                                        item.transactionId
                                                    }
                                                    isItemNotReceived={
                                                        payload.inr
                                                    }
                                                    isPositive={
                                                        payload.isPositive ===
                                                        true
                                                    }
                                                    isNegative={
                                                        payload.isPositive ===
                                                        false
                                                    }
                                                    ratingSettings={
                                                        ratingSettings
                                                    }
                                                    ratings={payload.ratings}
                                                    unpaidItemUrl={item.upiUrl}
                                                    {...item}
                                                    className="px-md-4 mb-4"
                                                />
                                            </Fragment>
                                        );
                                    })}
                                </div>
                            ))}
                            {nextCount > 0 && (
                                <div className="d-flex justify-content-center mb-4">
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={fetchNext}>
                                        {t('leaveFeedback_showMore', {
                                            count: nextCount
                                        })}
                                    </button>
                                </div>
                            )}
                            {isInMultipleFeedbacksContext ? (
                                <Breakpoint downTo={BREAKPOINTS.MD}>
                                    <div className="d-flex justify-content-center">
                                        <SubmitButton
                                            isSubmitting={isSubmitting}
                                            isInMultipleFeedbacksContext={
                                                isInMultipleFeedbacksContext
                                            }
                                            onClick={submitOrConfirm}
                                            submittableCount={
                                                submittables.length
                                            }
                                        />
                                    </div>
                                </Breakpoint>
                            ) : (
                                <div className="row mx-3 mx-md-0 mb-3">
                                    <button
                                        className="btn btn-outline-danger btn-sm col-12 col-sm-5 offset-sm-1 col-md-4 offset-md-2"
                                        type="button"
                                        onClick={() =>
                                            onDismiss && onDismiss()
                                        }>
                                        <CloseIcon
                                            className={`icon icon-sm mr-1`}
                                        />
                                        {t('leaveFeedback_abort')}
                                    </button>
                                    <SubmitButton
                                        className="col-12 col-sm-5 ml-sm-2 col-md-4 mt-2 mt-sm-0"
                                        isSubmitting={isSubmitting}
                                        isInMultipleFeedbacksContext={
                                            isInMultipleFeedbacksContext
                                        }
                                        onClick={submitOrConfirm}
                                        submittableCount={submittables.length}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Layout>
            )}
        </>
    );
};
